import * as ko from "knockout";
import * as _ from 'underscore';

import 'lockr';
import 'pubsub';

import {LOCAL_STORAGE} from 'Core/Common/Enums/LocalStorageItems';
import {GlobalManager, GLOBALS} from 'Core/GlobalManager/GlobalManager';

import {ProfilePage} from 'Core/ProfilePage/ProfilePage';
import {AboutPage} from 'Core/ProfilePage/AboutPage/AboutPage';
import {Deduplication} from 'Core/ProfilePage/Deduplication/Deduplication';
import {Downloads} from "Core/ProfilePage/Downloads/Downloads";

import {UserManager, UserRoles, UserTypes} from 'User/UserManager';
import {BlockUI} from 'Core/Common/BlockUi';
import {UserMenuStore} from 'MenuManager/Stores/UserMenuStore';
import {UserVarsManager} from 'Core/UserVarsManager/UserVarsManager';
import {Notifier} from 'Core/Common/Notifier';
import {AuthManager} from 'Auth/AuthManager';
import {LockScreen} from 'LockScreen/LockScreen';
import {Event} from 'Core/Common/Event';
import {EVENTS} from 'MenuManager/UserMenu/Events';
import {PUB_SUB_EVENTS} from 'MenuManager/PubSubEvents';
import {HelpBook} from 'HelpBook/HelpBook';
import {ReportIssueModal} from 'Core/Components/ReportIssueModal/ReportIssueModal';
import {LockManager} from "Core/Components/Locker/LockManager";
import {NOTIFICATIONS, LABELS} from "../../Core/Components/Translation/Locales";
import {IScreen} from "Core/Screens/IScreen";
import {IControl} from 'Core/Controls/IControl';
import {Timer} from "Core/Controls/Timer/Timer";
import {IActiveTimer} from "Core/Controls/Timer/Models/TimerModel";
import {
    ConfirmationDialog, EVENTS as CONFIRMATION_DIALOG_EVENTS,
    Types as ConfirmationTypes
} from "Core/Components/Dialogs/ConfirmationDialog/ConfirmationDialog";

import UserMenuTemplate from 'MenuManager/UserMenu/Templates/UserMenu.html';
import { Modal } from "../../Core/Common/Modal";
import { WikiJS } from "../Wiki/Wiki";
import { User } from "../../Core/Components/SecurityEditor/Models/SecurityEditorModel";

ko.templates['MenuManager/UserMenu/Templates/UserMenu'] = UserMenuTemplate;

const GUEST_USER_NAME = 'Login';

export class UserMenu extends Event {
    IsAuthenticated: KnockoutObservable<boolean>;
    IsAvailable: KnockoutObservable<boolean>;
    IsDeduplicationAllowed: KnockoutObservable<boolean>;
    private _userName: KnockoutObservable<string>;
    private _userImage: KnockoutObservable<string>;
    private _authManager: AuthManager;
    private _showLoginPopup: boolean;
    private _labels = LABELS;
    private _showMySettings: KnockoutObservable<boolean>;
    private _showWiki: KnockoutObservable<boolean>;

    constructor() {
        super();
        this.IsAuthenticated = ko.observable(false);
        this.IsAvailable = ko.observable(false);
        this.IsDeduplicationAllowed = ko.observable(false);
        this._showMySettings = ko.observable(false);
        this._authManager = new AuthManager();
        this._showLoginPopup = true;
        this._userName = ko.observable(GUEST_USER_NAME);
        this._userImage = ko.observable(this.GetDefaultUserImage());
        this._showWiki = ko.observable(false);

        this.AddEvent(EVENTS.SIGN_IN);
        this.AddEvent(EVENTS.SIGN_OUT);

        this.IsAuthenticated.subscribe(newValue => {
            if (newValue) {
                this.RefreshUser();
                this._showMySettings(Boolean(parseInt(GlobalManager.Instance.GetGlobal(GLOBALS.MY_SETTINGS))));
            } else {
                this._userName(GUEST_USER_NAME);
                this.IsAvailable(false);
            }
        });

        this._authManager.On(EVENTS.SIGN_IN, this, (eventArgs: any) => {
            if (eventArgs.data && eventArgs.data.AuthToken) {
                this.Trigger(EVENTS.SIGN_IN, {AuthToken: eventArgs.data.AuthToken});
                this._showMySettings(Boolean(parseInt(GlobalManager.Instance.GetGlobal(GLOBALS.MY_SETTINGS))));
            }
        });
    }

    static SetNewTitle(title?: string, licenseName?: string) {
        if (title && licenseName) {
            return document.title = title + ' - ' + licenseName;
        } else if (title) {
            return document.title = title;
        } else {
            return document.title = UserManager.Instance.CurrentUser.LicenseName; //default Title Name
        }
    }

    CheckDeduplicationAllowance() {
        let userManager = UserManager.Instance;
        let isInRoleUs = userManager.IsUserInRole(UserRoles.US);

        this.IsDeduplicationAllowed(isInRoleUs);
    }

    RefreshUser() {
        const userManager = UserManager.Instance;
        this._showWiki = ko.observable(userManager.IsUserInRole(UserRoles.DesignerLevel2) || userManager.IsUserInRole(UserRoles.DesignerLevel3));
        this._userName(userManager.CurrentUser.Name);
        this.CheckDeduplicationAllowance();

        const isAvailable = !userManager.CurrentUser.HasAnyType([UserTypes.Portal, UserTypes.Guest, UserTypes.Gdpr]);

        if (isAvailable) {
            let userImage = UserManager.Instance.CurrentUser.UserImage;
            if (userImage && userImage !== 'data:image/jpeg;base64,') {
                this._userImage(userImage);
            } else {
                this._userImage(this.GetDefaultUserImage());
            }
        }

        this.IsAvailable(isAvailable);
    }

    AutoStopActiveTimer(screen: IScreen, timerControl: Timer): void {
        if (screen.AllowTimerAutoStop && timerControl.IsRunTime && timerControl.IsConsultScreen ) {
            if (timerControl.TimerAutostart && timerControl.HasTimerStarted()) {
                const stopTimer: IActiveTimer = _.find(timerControl.ActiveTimers(), timer=> timer.RecordId === timerControl.TimerRecordId && timer.EntityId === timerControl.TimerEntityId);
                const confirmationDialog = new ConfirmationDialog({
                    Text: LABELS.STOP_THE_TIMER_FOR_BEFORE_LOGOUT.replace('{recordName}', `<b>${stopTimer.SubjectName}</b>`),
                    Type: ConfirmationTypes.Question,
                    TextConfirm: LABELS.YES,
                    TextDecline: LABELS.NO
                });
                confirmationDialog.On(CONFIRMATION_DIALOG_EVENTS.CONFIRM_SELECTED, this, () => {
                    timerControl.StopTimer(timerControl.TimerEntityId, timerControl.TimerRecordId);
                    this.SignOut();
                });
                confirmationDialog.On(CONFIRMATION_DIALOG_EVENTS.DISCARD_SELECTED, this, () => {
                    this.SignOut();
                });

                confirmationDialog.Show();
            }
        }
    }

    IsStoppedExitApp(selectedScreen: IScreen): boolean {
        let isStoppedExitApp: boolean = null;

        if (selectedScreen && selectedScreen.GetRecordId() ) {
            let timerControl: IControl = selectedScreen.GetControl('Timer');
            if (timerControl instanceof Timer) {
                if (timerControl.TimerAutostart && timerControl.HasTimerStarted()) {
                    isStoppedExitApp = true;
                    this.AutoStopActiveTimer(selectedScreen, timerControl);
                }
            }
        }
        return isStoppedExitApp;
    }

    SignOut() {
        LockManager.Instance.ReleaseAllLocks();
        BlockUI.Block();
        this._authManager.SignOut()
            .then(() => this.Trigger(EVENTS.SIGN_OUT, {FileDir: Lockr.get(LOCAL_STORAGE.FILE_DIR)}))
            .always(() => {
                    BlockUI.Unblock();
                }
            );
    }

    Logout() {
        let menuManager = require('MenuManager/MenuManager'),
            isSelectedScreen = menuManager.MenuManager.Instance.GetCurrentScreen(),
            isStoppedExitApp: boolean = this.IsStoppedExitApp(isSelectedScreen);

        if (!isStoppedExitApp) {
            this.SignOut();
        }
    }

    GetTemplateName() {
        return 'MenuManager/UserMenu/Templates/UserMenu';
    }

    GetShowLoginPopup() {
        return this._showLoginPopup;
    }

    SetShowLoginPopup(value: boolean) {
        this._showLoginPopup = value;
    }

    Login() {
        const menuManager = require('MenuManager/MenuManager');

        menuManager.MenuManager.Instance.HideGuestHomePage();
        menuManager.MenuManager.Instance.DeleteAreas();

        this._authManager.ShowAuthForm();
    }

    ResetPassword() {
        const promise = this._authManager.ShowAuthForm();
        if (promise) {
            promise.then(() => this._authManager.ShowResetPasswordForm());
        }
    }

    MySettings() {
        LockManager.Instance.ReleaseAllLocks();
        var profilePage = new ProfilePage();
        profilePage.On('PROFILE_CARD_IMAGE_CLICKED', this, (eventArgs) => {
            this.GetMyContact();
        });
        profilePage.RenderByTargetId('viewBody');

        const menuManager = require('MenuManager/MenuManager');
        menuManager.MenuManager.Instance.ResetScreenStyle();

        UserMenu.SetNewTitle();
    }

    AboutPage() {
        LockManager.Instance.ReleaseAllLocks();
        var aboutPage = new AboutPage();
        aboutPage.RenderByTargetId('viewBody');

        const menuManager = require('MenuManager/MenuManager');
        menuManager.MenuManager.Instance.ResetScreenStyle();

        UserMenu.SetNewTitle();
    }

    Deduplication() {
        LockManager.Instance.ReleaseAllLocks();
        var deduplication = new Deduplication();
        deduplication.RenderByTargetId('viewBody');

        const menuManager = require('MenuManager/MenuManager');
        menuManager.MenuManager.Instance.ResetScreenStyle();

        UserMenu.SetNewTitle();
    }

    Downloads() {
        var downloads = new Downloads();
        downloads.Render();
    }

    HelpPage() {
        LockManager.Instance.ReleaseAllLocks();
        let menuManager = require('MenuManager/MenuManager');
        var helpBook = HelpBook.Instance;
        let isSelectedScreen = menuManager.MenuManager.Instance.GetCurrentScreen();
        if (isSelectedScreen) {
            helpBook.ShowWithUserMenu(isSelectedScreen.GetId());
        } else {
            helpBook.Show();
        }
    }

    ShowReportIssueModal() {
        const menuManager = require('MenuManager/MenuManager');
        menuManager.MenuManager.Instance.ResetScreenStyle();

        ReportIssueModal.Open();
    }

    NavigateToHomePage() {
        const menuManager = require('MenuManager/MenuManager');
        menuManager.MenuManager.Instance.NavigateToHomePage();
    }

    GetMyContact() {
        LockManager.Instance.ReleaseAllLocks();
        let contactId = UserManager.Instance.CurrentUser.ContactId;
        BlockUI.Block();
        UserMenuStore.GetMetadata()
            .always(() => {
                BlockUI.Unblock();
            })
            .then(result => {
                if (!result.IsSuccessfull) {
                    new Notifier().Failed(result.ErrorMessage);
                    return;
                }

                let contactScreenMetadata = result.ResultObject.ContactScreenMetadata;

                UserMenu.SetNewTitle(contactScreenMetadata.TranslatedEntityName ? contactScreenMetadata.TranslatedEntityName : contactScreenMetadata.Name, UserManager.Instance.CurrentUser.LicenseName);

                if (!contactScreenMetadata) {
                    new Notifier().Warning(NOTIFICATIONS.CONTACT_SCREEN_NOT_FOUND);
                    return;
                }

                UserVarsManager.Instance.AddRecent(contactScreenMetadata.EntityId, contactId, 0);

                PubSub.publish(PUB_SUB_EVENTS.GO_TO_RECORD_SCREEN, {
                    EntityId: contactScreenMetadata.EntityId,
                    RecordId: contactId,
                    RecordTypeId: 0
                });
            });
    }

    LockSession() {
        LockManager.Instance.ReleaseAllLocks();
        var lockScreen = new LockScreen();
        lockScreen.Lock();
        UserMenu.SetNewTitle('Spheeres.com');
    }

    Render(target: string): void {
        var targetElement = document.getElementById(target);
        if (targetElement != null) {
            ko.cleanNode(targetElement);
            ko.applyBindings(this, targetElement);
        }
    }

    GetDefaultUserImage(): string {
        return "data:image/jpeg;base64,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";
    }

    Wiki(){
        const wiki = new WikiJS();
        wiki.Show();
    }
}